
import store from "@/store";
import AppLogo from "@/components/AppLogo.vue";
import { defineComponent } from "vue";
import { mapState } from "vuex";
import AES from "crypto-js/aes";
import CryptoJS from "crypto-js";
import AndroidBridge from "@/AndroidBridge";
import IOSBridge from "@/IOSBridge";
import { getAuth0Client, buildRedirectUri } from "@/lib/auth0";
import { persistDeepLinkFromLocation, resolveDeepLinkQuery, readDeepLinkQuery, clearDeepLinkQuery, saveDeepLinkQuery, pickDeepLinkQuery } from "@/lib/deepLink";
import { waitForNetworkReady, openExternalLink, hideBootSplash, isNativeWebView } from "@/lib/AppUtils";
import { jwtDecode } from "jwt-decode";
import { ArrowLeftIcon, ExclamationTriangleIcon, ComputerDesktopIcon, QuestionMarkCircleIcon, UserCircleIcon } from "@heroicons/vue/24/outline";

export default defineComponent({
  created() {
    if (this.$cookies.get("username")) {
      this.username = AES.decrypt(decodeURI(this.$cookies.get("username")), "2r5u8x/A").toString(CryptoJS.enc.Utf8);
    }
    if (this.$cookies.get("password")) {
      this.password = AES.decrypt(decodeURI(this.$cookies.get("password")), "2r5u8x/A").toString(CryptoJS.enc.Utf8);
    }
    if (this.$cookies.get("account_name")) {
      this.account_name = AES.decrypt(decodeURI(this.$cookies.get("account_name")), "2r5u8x/A").toString(CryptoJS.enc.Utf8);
    }
    localStorage.remember_me = this.rememberme;
  },
  data() {
    return {
      username: "",
      password: "",
      account_name: "",
      displayaccount: false,
      accountSetupError: false,
      errors: "" as string,
      auth0Client: {} as any,
      rememberme: localStorage.remember_me == "true" ? true : false,
      // Watchdog: if the splash screen is still up after this long, the
      // auth flow is wedged (most commonly: existing users without a
      // refresh token whose silent regen + Auth0 logout both hang).
      // Force them out so they can at least re-login instead of staring
      // at the animated logo. Tracked here so beforeUnmount() / the
      // happy-path branches can cancel it.
      splashWatchdogId: 0 as number,
      SPLASH_WATCHDOG_MS: 30000,
    };
  },
  components: {
    AppLogo,
    ArrowLeftIcon,
    ExclamationTriangleIcon,
    ComputerDesktopIcon,
    QuestionMarkCircleIcon,
    UserCircleIcon,
  },
  computed: {
    ...mapState(["account", "notifyData"]),
  },
  async mounted() {
    hideBootSplash();

    const AndroidBridge = (await import("@/AndroidBridge")).default;
    if (AndroidBridge.isAndroidAvailable()) {
      AndroidBridge.unsetChannelId();
    } else if (IOSBridge.getInstance().isIOSAvailable()) {
      IOSBridge.getInstance().unsetChannelId();
    }
    const arrivingDeepLink = persistDeepLinkFromLocation(this.$route.query as Record<string, unknown>);
    const queryPropertyId = arrivingDeepLink.propertyid || (this.$route.query.propertyid as string);
    const queryChannelId = arrivingDeepLink.channelId || (this.$route.query.channelId as string);
    if (queryPropertyId && queryChannelId) {
      store.commit("SET_DIRECT_CHAT_PARAMS", { propertyid: queryPropertyId, channelId: queryChannelId });
    } else {
      const directChatParams = store.state.notifyData.directChatParams;
      if (directChatParams?.propertyid && directChatParams?.channelId) {
        store.commit("SET_DIRECT_CHAT_PARAMS", directChatParams);
      }
    }

    // ---------------------------------------------------------------------
    // Deep-link arrival on iOS Safari (NOT in native app / installed PWA):
    // when the user arrives from an email link with redirect* params, keep
    // the iOS Smart App Banner visible long enough for them to tap "OPEN".
    // The Auth0 redirect below is a full cross-domain navigation that
    // permanently dismisses the banner, so we extend the delay only for
    // this specific case.
    //
    // On iOS Safari you cannot programmatically open the app via a
    // same-domain Universal Link (Apple blocks it). The Smart App Banner
    // is the only mechanism, hence the longer wait.
    // ---------------------------------------------------------------------
    const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent) && !/CriOS/.test(navigator.userAgent);
    const search = window.location.search || "";
    const hasDeepLinkParams = /redirect(companyid|propertyid|guid|type)/i.test(search);
    const isInNativeOrPWA = AndroidBridge.isAndroidAvailable() || IOSBridge.getInstance().isIOSAvailable() || window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;

    const isAuth0Callback = /[?&]code=/.test(search) && /[?&]state=/.test(search);
    // Web processes the Auth0 callback immediately. Holding 6–20s here
    // leaves a white splash after login and can trip the 30s watchdog
    // while generatecustomtoken / getPresence are still in flight.
    const authDelayMs = isAuth0Callback || isInNativeOrPWA || isNativeWebView() ? 0 : isIOSSafari && hasDeepLinkParams && !isInNativeOrPWA ? 20000 : 6000;

    // Arm the splash-screen watchdog BEFORE kicking off the auth flow so
    // that even a hang inside the initial setTimeout / waitForNetworkReady
    // / getTokenSilently / Auth0 logout cannot leave the user stranded.
    this.armSplashWatchdog();

    setTimeout(() => {
      this.authLogin();
    }, authDelayMs);
  },
  beforeUnmount() {
    this.clearSplashWatchdog();
  },
  methods: {
    openSupportLink() {
      openExternalLink("https://hellospoke.my.site.com/support/s/");
    },

    // ------------------------------------------------------------------
    // Splash-screen watchdog
    // ------------------------------------------------------------------
    // Existing users who logged in before token rotation was deployed
    // don't have an Auth0 refresh token cached under the new
    // "openid profile email read:user offline_access" scope key. The
    // first server-JWT key rotation (~10 min) then makes
    // getTokenSilently() fail with missing_refresh_token, and the
    // recovery path here calls auth0Client.logout() — which has no
    // timeout and can stall on flaky network / MAUI webview. Without
    // this watchdog the user sits on the animated splash indefinitely.
    armSplashWatchdog() {
      this.clearSplashWatchdog();
      this.$data.splashWatchdogId = window.setTimeout(() => {
        this.handleSplashTimeout();
      }, this.$data.SPLASH_WATCHDOG_MS);
    },
    clearSplashWatchdog() {
      if (this.$data.splashWatchdogId) {
        window.clearTimeout(this.$data.splashWatchdogId);
        this.$data.splashWatchdogId = 0;
      }
    },
    async handleSplashTimeout() {
      console.warn("[LoginView] Splash watchdog fired — auth flow appears stuck, forcing logout so the user can re-login");
      try {
        store.commit("RESET_APP_STATE");
      } catch (_e) {
        /* ignore */
      }
      try {
        localStorage.removeItem("access_token");
      } catch (_e) {
        /* ignore */
      }
      // Try a graceful Auth0 logout (so the Auth0 session itself is
      // cleared), but DO NOT wait on it — if logout is the thing that's
      // hung, we'd just be stuck again. Whichever finishes first wins.
      try {
        const client = this.$data.auth0Client && typeof this.$data.auth0Client.logout === "function" ? this.$data.auth0Client : await getAuth0Client();
        await Promise.race([
          client.logout({
            logoutParams: {
              prompt: "login",
              returnTo: window.location.origin + "/auth0login",
            },
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error("watchdog_logout_timeout")), 5000)),
        ]);
      } catch (logoutErr) {
        console.warn("[LoginView] Watchdog logout failed/timed out, falling back to hard reload", logoutErr);
        window.location.replace(window.location.origin + "/auth0login");
      }
    },

    validateForm() {
      this.errors = "";
      if (this.username && this.password && this.account_name) return true;
      if (!this.account_name) this.errors = "Account name is required.";
      if (!this.password) this.errors = "Password is required.";
      if (!this.username) this.errors = "Name is required.";
    },

    resetUserData() {
      store.commit("RESET_APP_STATE");
    },

    usernamefocus() {
      this.displayaccount = false;
    },

    async isAuthenticated() {
      const auth0Client = this.$data.auth0Client;
      return await auth0Client.isAuthenticated();
    },

    async initAuth() {
      // Use the shared singleton so this view and the global axios
      // interceptor (in store.ts) share Auth0 state and cache. Creating a
      // second client here would split caches and cause spurious "not
      // authenticated" results during silent token regen.
      this.$data.auth0Client = await getAuth0Client();
      return this.$data.auth0Client;
    },

    async loginnew() {
      try {
        const deepLink = persistDeepLinkFromLocation(this.$route.query as Record<string, unknown>);
        const auth0Client = this.$data.auth0Client;
        await auth0Client.loginWithRedirect({
          appState: deepLink,
          authorizationParams: {
            prompt: "login",
            screen_hint: "login",
            redirect_uri: buildRedirectUri(),
            // offline_access is REQUIRED for Auth0 to issue a refresh
            // token. Without it, getTokenSilently() in the global axios
            // interceptor (store.ts) fails with "Missing Refresh Token"
            // every time the server rotates JWT signing keys (~10 min),
            // which forces the user back to the Auth0 redirect login
            // mid-session. Must match the scope set as the default in
            // lib/auth0.ts so the SDK's refresh-token cache key lines up.
            scope: "openid profile email read:user offline_access",
          },
        });
      } catch (err) {
        console.error("Login failed:", err);
        throw err;
      }
    },

    async getusernew() {
      const user = await this.$data.auth0Client.getUser();
      return user;
    },

    async authLogin() {
      await waitForNetworkReady();
      await this.initAuth();

      const auth0Client = this.$data.auth0Client;
      if ((await this.isAuthenticated()) || (window.location.search.includes("code=") && window.location.search.includes("state="))) {
        try {
          if (window.location.search.includes("code=") && window.location.search.includes("state=")) {
            persistDeepLinkFromLocation(this.$route.query as Record<string, unknown>);
            const callbackResult = await auth0Client.handleRedirectCallback();
            if (callbackResult?.appState) {
              saveDeepLinkQuery(pickDeepLinkQuery(callbackResult.appState as Record<string, unknown>));
            }
            // Strip only Auth0 callback params. A pathname-only replaceState
            // is intercepted by Vue Router 4 and wipes $route.query, which
            // is how cold logins lost redirect* and fell through to
            // /property-list.
            const kept = resolveDeepLinkQuery(new URLSearchParams(window.location.search), this.$route.query as Record<string, unknown>, readDeepLinkQuery());
            const query: Record<string, string> = {};
            for (const [key, value] of Object.entries(kept)) {
              if (value) query[key] = value;
            }
            await this.$router.replace({ path: this.$route.path, query });
          }

          const newAuth = await Promise.race([auth0Client.getTokenSilently(), new Promise((_, reject) => setTimeout(() => reject(new Error("Token refresh timeout")), 10000))]);
          window.localStorage.setItem("access_token", newAuth);

          var newu = await this.getusernew();
          // Try the custom_token claim baked into the Auth0 user metadata
          // first (this is the fast/normal path for already-onboarded
          // users). If that claim is missing or carries the placeholder
          // "new_token", silently mint a fresh server JWT from the backend
          // instead of bouncing the user back to Auth0 — Auth0 itself just
          // confirmed (above) that this user is authenticated, so a
          // missing/stale claim is recoverable, not fatal.
          let id_token: string = newu["https://lou-dev-notify.hellospoke.com/custom_token"];
          if (!id_token || id_token === "new_token") {
            id_token = await store.dispatch("generatecustomtokeninauth0", {
              email: newu.email,
            });
          }
          if (!id_token) {
            throw new Error("missing_token");
          }
          const decoded: any = await jwtDecode(id_token);

          store.commit("LOGIN_SUCCESS", { data: { token: `${id_token}` } });
          store.commit("SET_ACCOUNT_ID", decoded.account_id);

          this.$data.username = newu.email;
          await this.doLogin(id_token);
        } catch (err) {
          // Reaching here means Auth0 itself is no longer authenticating
          // this user (refresh token expired/revoked, network outage on
          // /oauth/token, etc.). Force them back through Auth0 Universal
          // Login.
          console.error("Auth login failed:", err);
          localStorage.removeItem("access_token");
          await this.doLogout();
        }
      } else {
        this.loginnew();
      }
    },

    async doLogout() {
      this.accountSetupError = false;
      localStorage.removeItem("access_token");
      // Bound the Auth0 logout call so a stalled /v2/logout request
      // (network blip, MAUI throttle, Auth0 latency) can never leave
      // the user pinned on the splash screen. If it times out we fall
      // back to a hard replace — the watchdog's logout path does the
      // same, this just gets us there faster on the normal failure
      // path inside authLogin()'s catch.
      try {
        await Promise.race([
          this.$data.auth0Client.logout({
            logoutParams: {
              prompt: "login",
              returnTo: window.location.origin + "/auth0login",
            },
          }),
          new Promise((_, reject) => setTimeout(() => reject(new Error("logout_timeout")), 8000)),
        ]);
      } catch (logoutErr) {
        console.warn("[LoginView] Auth0 logout failed/timed out, falling back to hard reload", logoutErr);
        window.location.replace(window.location.origin + "/auth0login");
      }
    },

    async doLogin(token: string) {
      const username = this.username.toLowerCase();
      try {
        let localDirectChatParams = store.state.notifyData.directChatParams;
        const deepLink = resolveDeepLinkQuery(this.$route.query as Record<string, unknown>, readDeepLinkQuery());
        const queryPropertyId = deepLink.propertyid;
        const queryChannelId = deepLink.channelId;
        if (queryPropertyId && queryChannelId) {
          localDirectChatParams = { propertyid: queryPropertyId, channelId: queryChannelId };
          store.commit("SET_DIRECT_CHAT_PARAMS", localDirectChatParams);
        }

        store.commit("SET_USERNAME", username);
        localStorage.username = username;
        store.commit("RESET_REDIRECT_STATE");
        var redirecttype = deepLink.redirecttype;
        var redirectcompanyid = deepLink.redirectcompanyid;
        var redirectpropertyid = deepLink.redirectpropertyid;
        var redirectguid = deepLink.redirectguid;
        const p_response = (await store.dispatch("getPresence", { username: username })) as any;

        (window as any).pendo.initialize({
          visitor: {
            id: p_response.data.id,
            email: p_response.data.email,
            first_name: p_response.data.first_name,
            last_name: p_response.data.last_name,
            role: p_response.data.user_type ? p_response.data.user_type : "HS Employee",
            property: p_response.data.primarykazooaccount ? p_response.data.primarykazooaccount.name : "NA",
            account: p_response.data.primarykazooaccount ? p_response.data.primarykazooaccount.id : "NA",
            mgmt_company: p_response.data.accountname ? p_response.data.accountname : "NA",
            title: p_response.data.title ? p_response.data.title : "NA",
            creationDate: new Date(),
          },
          account: {
            id: p_response.data.primarykazooaccount ? p_response.data.primarykazooaccount.id : "NA",
            name: p_response.data.primarykazooaccount ? p_response.data.primarykazooaccount.name : "NA",
          },
        });

        if (AndroidBridge.isAndroidAvailable()) {
          AndroidBridge.sendUserData({ user_details: p_response.data, user_auth_token: token });
        } else if (IOSBridge.getInstance().isIOSAvailable()) {
          IOSBridge.getInstance().sendUserData({ user_details: p_response.data, user_auth_token: token });
        }
        store.commit("SET_NOTIFY_USER_TYPE", p_response.data);
        store.commit("SET_CURRENT_USER_DETAIL", p_response.data);
        store.commit("SET_REDIRECT", false);

        if (p_response.data.livereplysetting && p_response.data.livereplysetting.length > 0) {
          store.commit("SET_IS_RESTRICTED_USER", false);
          store.commit("SET_LOGIN_COMPANY_ID", p_response.data.companyid ? p_response.data.companyid : p_response.data.cid);
          store.commit("SET_NOTIFY_USER_PROPERTYLIST", p_response.data);
          if (redirecttype && redirectcompanyid) {
            store.commit("SET_REDIRECT_TYPE", redirecttype);
            store.commit("SET_REDIRECT_PROPERTY_ID", redirectpropertyid);
            store.commit("SET_REDIRECT_COMPANY_ID", redirectcompanyid);
            store.commit("SET_REDIRECT_GUID", redirectguid);
            store.commit("SET_REDIRECT", true);
            p_response.data.companyid = redirectcompanyid;
            clearDeepLinkQuery();
            if (redirecttype == "wo") {
              this.$router.replace("/workorder");
            } else if (redirecttype == "ca") {
              this.$router.replace("/ico");
            }
          } else {
            clearDeepLinkQuery();
            if (localDirectChatParams?.propertyid?.trim() && localDirectChatParams?.channelId?.trim()) {
              this.$router.replace(`/direct-chat/${localDirectChatParams.propertyid}/${localDirectChatParams.channelId}`);
            } else {
              this.$router.replace("/property-list");
            }
          }
        } else {
          store.commit("SET_IS_RESTRICTED_USER", true);
          this.accountSetupError = true;
          // Switching to the account-setup error UI is a terminal state
          // for this view (user has to act); cancel the watchdog so it
          // doesn't yank them to a logout while they're reading it.
          this.clearSplashWatchdog();
        }
      } catch (error) {
        console.error(error);
        localStorage.removeItem("access_token");
        await this.doLogout();
      }
    },
  },
});
